import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";

import { showError } from "redux/actions/ui";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague } from "redux/reducers/models/league";

import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import DataTable from "../../../../../components/dataTable/DataTable";
import { IPrizeAccount } from "./PrizeAccount";
import { GetCreditBook } from "api/rpc/2024-04/facilityAdmin/client/admin/creditBook";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import { Badge } from "components/badge/Badge";

import Search from "components/search/Search";
import DropFilter from "components/dropdown/DropFilter";

const PRIZE_ACCOUNT_STATUS_FILTER_KEY = "prize-account-status-filter";

export default function PrizeAccounts() {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [prizeAccounts, setPrizeAccounts] = useState<IPrizeAccount[]>(undefined);

  const [leagues, setLeagues] = useState<Array<ILeague>>([]);
  const [tournaments, setTournaments] = useState<Array<ITournament>>([]);
  const { displayCurrency } = useCurrencyFacility();

  const [filters, setFilters] = useState({
    search: undefined,
    limit: 50,
    offset: 0,
    extended: {},
    closed: null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadPrizeAccounts(source.token);

    return () => source.cancel();
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (filters.search !== undefined && prizeAccounts) {
      void loadTournaments(source.token);
      void loadLeagues(source.token);
    }
    return () => source.cancel();
  }, [filters, filters.extended, prizeAccounts]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (filters.search !== undefined && prizeAccounts) {
      void loadPrizeAccounts(source.token);
    }
    return () => source.cancel();
  }, [filters.search, filters.closed]);

  async function loadPrizeAccounts(token?: CancelToken) {
    if (prizeAccounts !== undefined) {
      setPrizeAccounts(undefined);
    }
    const { status, data } = await GetCreditBook({ type: "prize_account", search: filters.search, closed: filters.closed}, token ? false : true, token);

    console.log("GetCreditBook", status, data);
    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error getting prize accounts")));
    }

    setPrizeAccounts(status !== StatusCode.OK ? [] : data as IPrizeAccount[]);
  }

  async function loadTournaments(token?: CancelToken) {
    if (!prizeAccounts) {
      return;
    }

    const tournamentIds = prizeAccounts
      ?.filter(prizeAccount => prizeAccount?.tournament_id)
      ?.map(prizeAccount => {
        if (prizeAccount.tournament_id) {
          return prizeAccount.tournament_id;
        }
      });

    const { status, data } = await GetTournament({ ids: [...new Set(tournamentIds)] }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tournaments")));
      return;
    }

    setTournaments(data);
  }

  async function loadLeagues(token?: CancelToken) {
    if (!prizeAccounts) {
      return;
    }

    const leagueIds = prizeAccounts
      ?.filter(prizeAccount => prizeAccount?.league_id)
      ?.map(prizeAccount => {
        if (prizeAccount.league_id) {
          return prizeAccount.league_id;
        }
      });

    const { status, data } = await GetLeague({ ids: [...new Set(leagueIds)] as number[] }, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading leagues")));
      return;
    }

    setLeagues(data);
  }

  function applyFilter(
    filterValue: Array<Record<string, any>>,
  ) {
    const filter = filterValue[0];

    if (filterValue.length !== 1) {
      setFilters(prev => ({ ...prev, closed: null }));
    } else if (filter?.value === "closed") {
      setFilters(prev => ({ ...prev, closed: true }));
    } else if (filter.value === "open") {
      setFilters(prev => ({ ...prev, closed: false }));
    }
  }

  const primaryAction = {
    content: t("New Prize Account"),
    action: () => history.push("/admin/settings/prizeAccounts/new"),
  };

  return (
    <>
      <Page
        title={t("Prize Accounts")}
        primaryAction={primaryAction}
        narrow
      >
        <div className="flex flex-row flex-wrap gap-4 pb-4">
          <DropFilter
            title={t("Status")}
            filterData={[
              { value: "closed", title: t("Closed"), locked: false },
              { value: "open", title: t("Open"), locked: false },
            ]}
            filterLabelPropFromData="title"
            filterIdPropFromData="id"
            filterType="Checkbox"
            historyKey={PRIZE_ACCOUNT_STATUS_FILTER_KEY}
            applyFilters={filterReturn => applyFilter(filterReturn)}
          />
        </div>
        <div className="flex-grow mb-4">
          <Search
            searchCallback={searchValue => setFilters(prev => ({ ...prev, search: searchValue }))}
            historyKey="prize-accounts"
          />
        </div>
        <DataTable
          columns={[
            { label: t("Name") },
            { label: t("Event") },
            { label: t("Balance") },
            { label: "" },
          ]}
          loading={prizeAccounts === undefined}
        >
          {prizeAccounts?.map(account => {
            const prizeTournament = tournaments.filter(tourny => tourny.id === account.tournament_id);
            const prizeLeague = leagues.filter(league => league.id === account.league_id);

            return (
              <tr
                key={account.id}
                onClick={() => history.push("/admin/settings/prizeAccounts/view/" + String(account.id))}
                className="clickable"
              >
                <td>
                  <p>{account.title}</p>
                </td>
                <td>
                  <p>{prizeTournament[0] ? prizeTournament[0].name : prizeLeague[0] ? prizeLeague[0].name : ""}</p>
                </td>

                <td>
                  <p>{displayCurrency(account.balance, account?.currency)}</p>
                </td>

                <td>{account.closed ? <Badge type="error">{t('Closed')}</Badge>:<Badge type="success">{t('Open')}</Badge>}</td>
              </tr>
            );
          })}
        </DataTable>
      </Page>
    </>
  );
}
